/**
 * Centralized formatting. Never use raw .toLocaleString() / .toLocaleDateString()
 * in pages — go through these helpers so locale + tz handling stays consistent.
 */

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
});

const DATETIME_FMT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

const USD = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

function toDate(input: string | number | Date | null | undefined): Date | null {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(input: string | number | Date | null | undefined): string {
  const d = toDate(input);
  return d ? DATE_FMT.format(d) : '—';
}

export function fmtDateTime(input: string | number | Date | null | undefined): string {
  const d = toDate(input);
  return d ? DATETIME_FMT.format(d) : '—';
}

export function fmtCurrency(amount: number | null | undefined, currency = 'USD'): string {
  if (amount == null || Number.isNaN(amount)) return '—';
  if (currency === 'USD') return USD.format(amount);
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
}

const SMALL_UNIT_FMT = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const LARGE_UNIT_FMT = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 });

/**
 * Formats a magnitude expressed in a base unit (Wh or W), switching to the
 * "kilo" unit above 1000 for readability. Shared by fmtEnergyWh/fmtPowerW so
 * every stat tile, chart tick, and tooltip in the app applies the exact same
 * threshold and rounding — never hand-roll this per callsite.
 */
function fmtScaledUnit(value: number | null | undefined, baseUnit: string, kiloUnit: string): string {
  if (value == null || Number.isNaN(value)) return '—';
  if (Math.abs(value) < 1000) return `${SMALL_UNIT_FMT.format(value)} ${baseUnit}`;
  return `${LARGE_UNIT_FMT.format(value / 1000)} ${kiloUnit}`;
}

/** Watt-hours -> "kWh" above 1000 Wh, otherwise "Wh". Null/undefined/NaN render as an em dash, never 0. */
export function fmtEnergyWh(wh: number | null | undefined): string {
  return fmtScaledUnit(wh, 'Wh', 'kWh');
}

/** Watts -> "kW" above 1000 W, otherwise "W". Null/undefined/NaN render as an em dash, never 0. */
export function fmtPowerW(w: number | null | undefined): string {
  return fmtScaledUnit(w, 'W', 'kW');
}
